import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import useSWR from 'swr';
import dayjs, { type Dayjs } from 'dayjs';
import { Box, styled } from '@mui/material';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Label from '@/components/@IntranetLibrary/Label';
import Select from '@/components/@IntranetLibrary/Select';
import {
  LEAVE_BALANCE_HINTS,
  LeaveBalanceTable,
} from '@/components/Approval/@components/AnnualLeave';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import { SectionHint } from '@/components/Approval/@components/NewCorporateCard';
import { ResignationEmployeeTable } from '@/components/Approval/@components/Resignation';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { getAnnualLeaveRequestDraftSourceUrl } from '@/constant/approval';
import { MyInfoState } from '@/recoil/atom';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  ANNUAL_LEAVE_NO_BALANCE_MESSAGE,
  buildAnnualLeaveRequestSubject,
  buildLeaveReasonContent,
  changeEndsAt,
  changeStartsAt,
  changeStartsUnit,
  endUnitsOf,
  isMorningOnlyUnit,
  LEAVE_REASONS,
  LEAVE_UNIT_LABELS,
  startUnitsOf,
  WORK_START_HOURS,
  workTimeLabel,
  type AnnualLeaveDraftSource,
  type AnnualLeaveRequestFormState,
  type LeaveReason,
  type LeaveUnit,
} from '@/utils/approval/annualLeaveRequest';
import { VEHICLE_COST_HR_MISSING_MESSAGE } from '@/utils/approval/vehicleCost';
import { formatAsDate } from '@/utils/format';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const HrMissingNotice = styled(ApprovalNoticeBox)`
  color: var(--dk-danger, #f55060);
`;

const InlineRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 12px;
`;

const SubField = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Separator = styled(Box)`
  padding-bottom: 10px;
`;

const UseCount = styled('strong')`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  font-size: 16px;
  color: var(--dk-primary, #1e6cfa);
`;

const ErrorText = styled('span')`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  font-size: 14px;
  color: var(--dk-danger, #f55060);
`;

const HintList = styled(SectionHint)`
  margin: 8px 0 0;
`;

const toPickerValue = (value: string): Dayjs | null =>
  value ? dayjs(value) : null;

const toDateString = (value: Dayjs | null): string =>
  value?.isValid() ? formatAsDate(value) : '';

type Props = {
  /** 본문 에디터 mount 전에는 null — 휴가 사유를 골라도 본문을 못 바꿈 */
  editorApi: ReplaceContentApi | null;
  /** 편집 — 저장된 휴가 시작일 연도의 사용 현황(레거시 동일). 신규는 올해 */
  balanceYear?: number;
};

/** 연차휴가(32) 본문 — 레거시 appr_edit_forms_year_leave_v2 */
const AnnualLeaveRequestField = ({ editorApi, balanceYear }: Props) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'annualLeaveRequest' });
  const companyCode = useAtomValue(MyInfoState)?.companyCode;
  const [reason, setReason] = useState<LeaveReason>('A');

  const { data: source, error: sourceError } = useSWR<AnnualLeaveDraftSource>(
    state ? getAnnualLeaveRequestDraftSourceUrl({ year: balanceYear }) : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const balance = source?.display?.leaveBalance ?? null;
  const noBalance = !!source && !balance;

  useEffect(() => {
    const current = getValues('annualLeaveRequest');
    if (!current || current.noBalance === noBalance) return;
    setValue('annualLeaveRequest', { ...current, noBalance });
  }, [noBalance, getValues, setValue]);

  const { data: countSource, error: countError } =
    useSWR<AnnualLeaveDraftSource>(
      state && state.startsAt && state.endsAt && !noBalance
        ? getAnnualLeaveRequestDraftSourceUrl({
            startsAt: state.startsAt,
            startsUnit: state.startsUnit,
            endsAt: state.endsAt,
            endsUnit: state.endsUnit,
          })
        : null,
      getNestedDataFetcher,
      {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
        keepPreviousData: true,
      },
    );

  // 레거시 — 기간·구분을 바꿀 때마다 제목을 다시 씀(직접 고친 제목도 덮어씀)
  const update = useCallback(
    (
      next: (
        current: AnnualLeaveRequestFormState,
      ) => AnnualLeaveRequestFormState,
    ) => {
      const current = getValues('annualLeaveRequest');
      if (!current) return;
      const updated = next(current);
      setValue('annualLeaveRequest', updated, { shouldDirty: true });
      setValue('subject', buildAnnualLeaveRequestSubject(updated), {
        shouldDirty: true,
      });
    },
    [getValues, setValue],
  );

  if (!state) {
    return (
      <FieldBlock>
        <HrMissingNotice>{t(VEHICLE_COST_HR_MISSING_MESSAGE)}</HrMissingNotice>
      </FieldBlock>
    );
  }

  const { applicant } = state;
  const unitOptions = (units: LeaveUnit[]) =>
    units.map((unit) => ({ value: unit, label: t(LEAVE_UNIT_LABELS[unit]) }));
  const endLocked = isMorningOnlyUnit(state.startsUnit);
  const sameDay = state.startsAt === state.endsAt;
  const useCount = countSource?.display?.useCount;

  const handleReason = (value: LeaveReason) => {
    setReason(value);
    editorApi?.replace(buildLeaveReasonContent(value));
  };

  return (
    <Box>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{t('휴근자 정보')}</InfoSectionTitle>
        </FieldHeader>
        <ResignationEmployeeTable
          employee={{
            name: applicant.name,
            employeeNumber: applicant.employeeNumber,
            affiliation: applicant.department,
            enterDate: applicant.hireDate,
          }}
        />
      </FieldBlock>

      {noBalance ? (
        <FieldBlock>
          <ApprovalNoticeBox>
            {t(ANNUAL_LEAVE_NO_BALANCE_MESSAGE)}
          </ApprovalNoticeBox>
        </FieldBlock>
      ) : (
        <>
          <FieldBlock>
            <FieldHeader>
              <InfoSectionTitle>{t('휴가 사용 현황')}</InfoSectionTitle>
            </FieldHeader>
            {balance ? (
              <LeaveBalanceTable balance={balance} />
            ) : (
              <SectionHint>
                {sourceError ? t('데이터를 불러오지 못했습니다.') : '-'}
              </SectionHint>
            )}
            <HintList>
              {LEAVE_BALANCE_HINTS.map((hint) => (
                <span key={hint}>
                  {t(hint)}
                  <br />
                </span>
              ))}
            </HintList>
          </FieldBlock>

          <FieldBlock>
            <FieldHeader>
              <InfoSectionTitle>{t('휴가 신청 기간')}</InfoSectionTitle>
            </FieldHeader>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <SubField>
                <Label size={14} isRequired={false} title={t('근무시간')} />
                <Select
                  value={String(state.workStartHour)}
                  options={WORK_START_HOURS.map((hour) => ({
                    value: String(hour),
                    label: t('{{range}}시', { range: workTimeLabel(hour) }),
                  }))}
                  onChange={(e) => {
                    const workStartHour = Number(e.target.value);
                    update((current) => ({ ...current, workStartHour }));
                  }}
                  sx={{ ...approvalOutlineSelectSx, width: 180 }}
                />
              </SubField>
              <InlineRow>
                <SubField>
                  <Label size={14} isRequired={false} title={t('시작일')} />
                  <DatePicker
                    value={toPickerValue(state.startsAt)}
                    onChange={(value) =>
                      update((current) =>
                        changeStartsAt(current, toDateString(value)),
                      )
                    }
                    format="YYYY-MM-DD"
                    placeholder="yyyy-mm-dd"
                    sx={{ width: 160 }}
                  />
                </SubField>
                <SubField>
                  <Label size={14} isRequired={false} title={t('구분')} />
                  <Select
                    value={state.startsUnit}
                    options={unitOptions(startUnitsOf(companyCode))}
                    onChange={(e) =>
                      update((current) =>
                        changeStartsUnit(current, e.target.value as LeaveUnit),
                      )
                    }
                    sx={{ ...approvalOutlineSelectSx, minWidth: 140 }}
                  />
                </SubField>
                <Separator>~</Separator>
                <SubField>
                  <Label size={14} isRequired={false} title={t('종료일')} />
                  <DatePicker
                    value={toPickerValue(state.endsAt)}
                    onChange={(value) =>
                      update((current) =>
                        changeEndsAt(current, toDateString(value)),
                      )
                    }
                    minDate={toPickerValue(state.startsAt) ?? undefined}
                    disabled={endLocked}
                    format="YYYY-MM-DD"
                    placeholder="yyyy-mm-dd"
                    sx={{ width: 160 }}
                  />
                </SubField>
                <SubField>
                  <Label size={14} isRequired={false} title={t('구분')} />
                  <Select
                    value={state.endsUnit}
                    options={unitOptions(
                      endUnitsOf(companyCode, state.endsUnit),
                    )}
                    disabled={endLocked || sameDay}
                    onChange={(e) => {
                      const endsUnit = e.target.value as LeaveUnit;
                      update((current) => ({ ...current, endsUnit }));
                    }}
                    sx={{ ...approvalOutlineSelectSx, minWidth: 140 }}
                  />
                </SubField>
                <Separator>=</Separator>
                <SubField>
                  <Label size={14} isRequired={false} title={t('사용일수')} />
                  {countError ? (
                    <ErrorText>{t('데이터를 불러오지 못했습니다.')}</ErrorText>
                  ) : (
                    <UseCount>{useCount ?? '-'}</UseCount>
                  )}
                </SubField>
              </InlineRow>
            </Box>
          </FieldBlock>

          <FieldBlock>
            <FieldHeader>
              <InfoSectionTitle>{t('휴가 사유')}</InfoSectionTitle>
            </FieldHeader>
            <Select
              value={reason}
              options={LEAVE_REASONS.map((option) => ({
                value: option.value,
                label: t(option.label),
              }))}
              onChange={(e) => handleReason(e.target.value as LeaveReason)}
              sx={{ ...approvalOutlineSelectSx, width: 180 }}
            />
          </FieldBlock>
        </>
      )}
    </Box>
  );
};

export default AnnualLeaveRequestField;
